import {
  Box,
  Checkbox,
  Grid2,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import React, { useCallback, useState } from 'react'
import { ScrollTag } from 'utoniq-ui'

import { Item, ItemType } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ItemImage } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
export * from './fragments'
import { config } from '@/config'
import { encodeSkuId } from '@/core/sku'

import type { Action, State } from '../viewModel'

type ItemsCellProps = Pick<
  State,
  'organizationPortfolios' | 'loading' | 'hasNextPage'
> & {
  onFetchMore: Action['onFetchMore']
}

type FilterType = 'general' | ItemType

export const OwnedItemsTemplate: Template<State, Action> = ({
  state,
  onSort,
  onFetchMore,
  onFilter,
  onFilterResale,
}) => {
  const { t } = useTranslation('ownedItems')
  const { routerNames } = useRouter()

  const [currentFilter, setCurrnetFilter] = useState<FilterType>(state.filter)

  const handleSelectFilter = useCallback(
    (value: string) => {
      onFilter(value as FilterType)
      setCurrnetFilter(value as FilterType)
    },
    [onFilter]
  )

  return (
    <BaseLayout header={{ back: true, title: t('title') }}>
      <Head title={routerNames.MyOwnedItems} />
      <FilterMenu onSelect={handleSelectFilter} currentFilter={currentFilter} />
      <SortAndResaleFilter
        currentFilter={currentFilter}
        onSort={onSort}
        onFilterResale={onFilterResale}
      />
      <ItemsCell
        organizationPortfolios={state.organizationPortfolios}
        loading={state.loading}
        hasNextPage={state.hasNextPage}
        onFetchMore={onFetchMore}
      />
    </BaseLayout>
  )
}

export const FilterMenu = ({
  currentFilter,
  onSelect,
}: {
  currentFilter: FilterType
  onSelect: (value: string) => void
}) => {
  const { t } = useTranslation('ownedItems')

  return (
    <CellFrame sx={{ px: 5 }}>
      <ScrollTag
        value={currentFilter}
        onClick={onSelect}
        tags={[
          { value: 'general', label: t('general') },
          { value: ItemType.Video, label: t('video') },
          { value: ItemType.Audio, label: t('sound') },
          { value: ItemType.Wallpaper, label: t('wallpaper') },
          { value: ItemType.Ticket, label: t('ticket') },
          { value: ItemType.Magazine, label: t('magazine') },
          { value: ItemType.Merch, label: t('merch') },
        ]}
      />
    </CellFrame>
  )
}

const sorts = ['filterProcedure', 'filterArtist', 'filterDate']

const SortAndResaleFilter: Cell<{
  currentFilter: string
  onSort: Action['onSort']
  onFilterResale: Action['onFilterResale']
}> = ({ currentFilter, onSort, onFilterResale }) => {
  const { t } = useTranslation('ownedItems')
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleMenuItemClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, index: number) => {
      setSelectedIndex(index)
      setAnchorEl(null)
      onSort(index)
    },
    [onSort]
  )

  return (
    <CellFrame
      display="flex"
      justifyContent="space-between"
      sx={{ pb: 2, px: 4 }}
    >
      <Stack flexDirection="row" alignItems="center">
        {currentFilter === ItemType.Ticket && config.modules.resale && (
          <label htmlFor="filterResale">
            <Checkbox
              id="filterResale"
              sx={{ '&.Mui-checked svg': { color: 'primary.main' }, pr: 1 }}
              onChange={(_, checked) => {
                onFilterResale(checked)
              }}
            />
            <Typography variant="caption">{t('resaleFilter')}</Typography>
          </label>
        )}
      </Stack>
      {/* 入手順/アーティスト順/発売日順のフィルターAPI実装まで非表示にする */}
      {/*
      <Button
        variant="text"
        onClick={handleClick}
        sx={{ color: 'text.primary', typography: 'caption' }}
      >
        {t(sorts[selectedIndex])}
        <Icons.DownArrow style={{ fontSize: 24 }} sx={{ pl: 2 }} />
      </Button>
 <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {sorts.map((filter, index) => (
          <MenuItem
            key={index}
            sx={{ bgcolor: 'grey.50' }}
            selected={selectedIndex === index}
            onClick={event => handleMenuItemClick(event, index)}
          >
            {t(filter)}
          </MenuItem>
        ))}
      </Menu> */}
    </CellFrame>
  )
}

const ItemsCell: Cell<ItemsCellProps> = ({
  organizationPortfolios,
  loading,
  hasNextPage,
  onFetchMore,
}) => {
  const { t } = useTranslation('ownedItems')
  const { routerPages, generatePath, router } = useRouter()

  return (
    <CellFrame sx={{ px: 5, py: 2 }}>
      <ScrollFetcher
        nodes={organizationPortfolios?.edges?.map(item => (
          <Box
            key={[item.node.item.id, item.node.item.sku?.id].join('-')}
            sx={{ position: 'relative', pb: 2 }}
          >
            <ItemImage
              component="div"
              onClick={() =>
                router.push({
                  pathname: generatePath(routerPages.OwnedItemDetail, {
                    itemId: item.node.item.id,
                  }),
                  search: item.node.item.sku
                    ? `?skuId=${encodeSkuId(item.node.item.sku?.id ?? '')}`
                    : '',
                })
              }
              item={item.node.item as Item}
              ownedCount={item.node.number}
              showIcon
            />
          </Box>
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        component={Grid2}
        emptyMessage={t('portfolioEmptyMessage')}
        componentProps={{
          container: true,
          spacing: 3,
        }}
        itemComponent={Grid2}
        itemComponentProps={{
          size: { xs: 4, md: 3 },
          sx: { textAlign: 'center' },
        }}
        loadingComponent={
          <Grid2 container spacing={3}>
            {Array.from(Array(24).keys()).map(key => (
              <Grid2 size={{ xs: 4, md: 3 }} key={key}>
                <Skeleton
                  variant="rectangular"
                  sx={{ width: '100%', height: 166 }}
                />
              </Grid2>
            ))}
          </Grid2>
        }
      />
    </CellFrame>
  )
}
